import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Dumbbell, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const EMPTY_EX = { name: "", sets: "", reps: "", weight: "", rest: "" };
const EMPTY = { title: "", goal: "", notes: "", exercises: [{ ...EMPTY_EX }], status: "active" };

export default function WorkoutPrograms({ memberId, canEdit = false }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);

  const openTpl = () => { api.get("/workout-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([])); setTplOpen(true); };
  const assignTpl = async (tid) => {
    try { await api.post(`/members/${memberId}/workout-programs/from-template/${tid}`); toast.success("Program atandı"); setTplOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = () => api.get(`/members/${memberId}/workout-programs`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [memberId]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, exercises: [{ ...EMPTY_EX }] }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, goal: p.goal || "", notes: p.notes || "", status: p.status || "active",
      exercises: (p.exercises || []).map((e) => ({ name: e.name || "", sets: e.sets ?? "", reps: e.reps || "", weight: e.weight || "", rest: e.rest || "" })) });
    setOpen(true);
  };

  const setEx = (i, k, v) => setForm((f) => ({ ...f, exercises: f.exercises.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const addEx = () => setForm((f) => ({ ...f, exercises: [...f.exercises, { ...EMPTY_EX }] }));
  const removeEx = (i) => setForm((f) => ({ ...f, exercises: f.exercises.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title, goal: form.goal, notes: form.notes, status: form.status,
        exercises: form.exercises.filter((e) => e.name.trim()).map((e) => ({ name: e.name, sets: e.sets ? Number(e.sets) : null, reps: e.reps || null, weight: e.weight || null, rest: e.rest || null })),
      };
      if (editing) { await api.put(`/workout-programs/${editing.id}`, payload); toast.success("Program güncellendi"); }
      else { await api.post(`/members/${memberId}/workout-programs`, payload); toast.success("Program oluşturuldu"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/workout-programs/${id}`); toast.success("Program silindi"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (items === null) return <Skeleton className="h-48 rounded-2xl bg-[#13131C] mt-4" />;

  return (
    <div className="mt-4" data-testid="workout-programs-panel">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display font-bold text-white text-lg">Antrenman Programları</h4>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={openTpl} data-testid="assign-template-button" variant="secondary" className="bg-[#1C1C28] hover:bg-[#252536] text-white border border-[#2D2D3F] rounded-xl h-9">Şablondan Ata</Button>
            <Button onClick={openNew} data-testid="add-program-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl h-9"><Plus size={16} className="mr-1" /> Program Ekle</Button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-[#68687B] rounded-2xl border border-[#222230] bg-[#13131C]">
          <Dumbbell size={32} className="mx-auto mb-2 opacity-50" />Henüz antrenman programı yok.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`program-${p.id}`} className="rounded-2xl bg-[#13131C] border border-[#222230] p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="font-display font-bold text-white">{p.title}</h5>
                  {p.goal && <p className="text-xs text-[#FF5500] mt-0.5">Hedef: {p.goal}</p>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(p)} data-testid={`edit-program-${p.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#FF5500] hover:bg-[#252536]"><Pencil size={15} /></button>
                    <button onClick={() => del(p.id)} data-testid={`delete-program-${p.id}`} className="p-1.5 rounded-lg text-[#68687B] hover:text-[#EF4444] hover:bg-[#252536]"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto rounded-xl border border-[#1A1A24]">
                <table className="w-full text-sm">
                  <thead className="bg-[#181824] text-[#68687B] text-xs uppercase">
                    <tr><th className="px-3 py-2 text-left">Egzersiz</th><th className="px-3 py-2">Set</th><th className="px-3 py-2">Tekrar</th><th className="px-3 py-2">Ağırlık</th><th className="px-3 py-2">Dinlenme</th></tr>
                  </thead>
                  <tbody>
                    {(p.exercises || []).map((e, i) => (
                      <tr key={i} className="border-t border-[#1A1A24] text-center">
                        <td className="px-3 py-2 text-left font-medium text-white">{e.name}</td>
                        <td className="px-3 py-2 font-mono text-[#A0A0B2]">{e.sets ?? "-"}</td>
                        <td className="px-3 py-2 font-mono text-[#A0A0B2]">{e.reps || "-"}</td>
                        <td className="px-3 py-2 font-mono text-[#A0A0B2]">{e.weight || "-"}</td>
                        <td className="px-3 py-2 font-mono text-[#A0A0B2]">{e.rest || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {p.notes && <p className="text-xs text-[#68687B] mt-3">📝 {p.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Programı Düzenle" : "Yeni Program"}</DialogTitle><DialogDescription className="text-[#68687B]">Egzersizleri ekleyip üyeye özel program oluştur.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Program Adı</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="program-title" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Hedef</label><Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1"><label className="text-xs text-[#A0A0B2]">Egzersizler</label>
                <button onClick={addEx} type="button" data-testid="add-exercise-row" className="text-xs text-[#FF5500] font-semibold flex items-center gap-1"><Plus size={13} /> Ekle</button></div>
              <div className="space-y-2">
                {form.exercises.map((e, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <Input placeholder="Egzersiz" value={e.name} onChange={(ev) => setEx(i, "name", ev.target.value)} data-testid={`exercise-name-${i}`} className="col-span-4 h-9 bg-[#0E0E14] border-[#262636] text-white text-sm" />
                    <Input placeholder="Set" value={e.sets} onChange={(ev) => setEx(i, "sets", ev.target.value)} className="col-span-2 h-9 bg-[#0E0E14] border-[#262636] text-white text-sm" />
                    <Input placeholder="Tekrar" value={e.reps} onChange={(ev) => setEx(i, "reps", ev.target.value)} className="col-span-2 h-9 bg-[#0E0E14] border-[#262636] text-white text-sm" />
                    <Input placeholder="Ağırlık" value={e.weight} onChange={(ev) => setEx(i, "weight", ev.target.value)} className="col-span-2 h-9 bg-[#0E0E14] border-[#262636] text-white text-sm" />
                    <button type="button" onClick={() => removeEx(i)} className="col-span-2 h-9 flex items-center justify-center text-[#68687B] hover:text-[#EF4444]"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Notlar</label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0E0E14] border-[#262636] text-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.title} data-testid="program-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Hazır Şablonlar</DialogTitle><DialogDescription className="text-[#68687B]">Tek tıkla üyeye program ata.</DialogDescription></DialogHeader>
          <div className="space-y-2 py-1">
            {templates.length === 0 ? <p className="text-sm text-[#68687B] text-center py-6">Şablon bulunamadı.</p> : templates.map((t) => (
              <button key={t.id} onClick={() => assignTpl(t.id)} data-testid={`assign-template-${t.id}`}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#0E0E14] border border-[#262636] hover:border-[#FF5500]/50 transition-all text-left">
                <Dumbbell size={18} className="text-[#FF5500]" />
                <div className="flex-1"><p className="text-sm font-semibold text-white">{t.title}</p><p className="text-xs text-[#68687B]">{t.goal} · {(t.exercises || []).length} egzersiz</p></div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
